import { Component, EventEmitter, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { Validators } from '@angular/forms';
import { OrganisationDto } from '@assetaccountant/api/models/organisation.dto';
import { RegisterDto } from '@assetaccountant/api/models/register.dto';
import { QueueReportResponse } from '@assetaccountant/api/models/reports/queue-report.response';
import { ReportConsolidationAction } from '@assetaccountant/api/models/reports/report-consolidation-action';
import { ReportFormat } from '@assetaccountant/api/models/reports/report-format';
import { ReportApi } from '@assetaccountant/api/services/report.api';
import { CalendarService, toUniversalDateString } from '@assetaccountant/core';
import { ValidSubmitEvent } from '@assetaccountant/core/directives/forms/valid-submit.directive';
import { FinancialYearStart } from '@assetaccountant/core/models/financial-year-start';
import { RegimeApiModel } from '@assetaccountant/core/models/regime-api-model';
import { assertNever } from '@assetaccountant/core/utils/asset-utils';
import { emptyValue, TypedFormBuilder } from '@assetaccountant/core/utils/typed-forms';
import { ConsolidatedReportType } from '@assetaccountant/reports/models/consolidated-report-type';
import {
  ConsolidatedReportComponent,
  getReportSettings,
  ReportDuration,
  ReportSettings,
  updateComponent,
} from '@assetaccountant/reports/models/report-settings';
import { ReportType } from '@assetaccountant/reports/models/report-type';
import moment from 'moment';
import { combineLatest, Observable } from 'rxjs';
import { distinctUntilChanged, map } from 'rxjs/operators';

interface ReportFormValue {
  reportType: ConsolidatedReportType;
  format: ReportFormat;
  consolidationAction: ReportConsolidationAction;
  periodStartDate: string;
  periodEndDate: string;
  durationInYears: number;
  regimeId: number;
  groupingFieldId: string;
  externalEntityCode: string;
  includeAdditionalDisposalInfo: boolean;
}

@Component({
  selector: 'app-generate-consolidated-report',
  templateUrl: './generate-consolidated-report.component.html',
  styleUrls: ['./generate-consolidated-report.component.scss'],
  standalone: false,
})
export class GenerateConsolidatedReportComponent
  implements OnInit, OnDestroy, ConsolidatedReportComponent
{
  readonly ReportFormat = ReportFormat;
  readonly ReportConsolidationAction = ReportConsolidationAction;
  readonly ReportType = ReportType;

  readonly ConsolidatedReportTypes: ConsolidatedReportType[] = [
    ReportType.AssetExport,
    ReportType.AssetGroupSummary,
    ReportType.DepreciationForecast,
  ];

  @Input() organisation: OrganisationDto;
  @Input() registers: Array<RegisterDto & { _selected?: boolean }>;

  @Output() closePanel = new EventEmitter<string | null | undefined>();

  financialYearStart: FinancialYearStart;
  firstYearStartDate: string;
  endDates: string[] = [];
  startDates: string[] = [];

  firstSelectedRegister: RegisterDto;
  regimes: RegimeApiModel[];

  form = this.fb.group({
    periodStartDate: [emptyValue<string>(), Validators.required],
    periodEndDate: [emptyValue<string>(), Validators.required],
    reportType: [emptyValue<ConsolidatedReportType>(), Validators.required],
    regimeId: [emptyValue<number>(), Validators.required],
    format: this.fb.control(ReportFormat.Pdf, Validators.required),
    consolidationAction: this.fb.control(ReportConsolidationAction.Merge, Validators.required),
    durationInYears: [{ value: 1, disabled: true }, Validators.required],
    includeAdditionalDisposalInfo: [false],
  });

  dateType: 'RANGE' | 'DURATION' | 'START' = 'RANGE';
  disablePdf = false;
  durationDisablePdf = false;
  limitStartDate = false;
  filteredRegimes?: RegimeApiModel[];
  durations?: ReportDuration[];
  shouldShowIncludeAdditionalDisposalInfo = false;
  shouldShowConsolidationActions = false;

  private _subscriptions = [
    this.form.controls.reportType.valueChanges
      .pipe(distinctUntilChanged())
      .subscribe(reportType => {
        const settings = getReportSettings(reportType, {
          pdfAssetLimitExceeded: false,
        }) as ReportSettings & {
          dateType: 'RANGE' | 'DURATION';
        };

        updateComponent(this, settings);
      }),
    combineLatest([
      this.form.controls.durationInYears.statusChanges.pipe(distinctUntilChanged()),
      this.form.controls.durationInYears.valueChanges.pipe(distinctUntilChanged()),
    ])
      .pipe(
        map(([status, durationInYears]) =>
          status !== 'DISABLED'
            ? this.durations?.find(d => d.value === durationInYears)
            : undefined,
        ),
      )
      .subscribe(duration => {
        this.durationDisablePdf = !!duration && !duration.pdfAvailable;
        if (this.durationDisablePdf) {
          this.form.controls.format.setValue(ReportFormat.Csv);
        }
        this.limitStartDate = !!duration && duration.value > 1;
      }),
  ];

  get isForcedFormat() {
    return this.disablePdf || this.durationDisablePdf;
  }

  constructor(
    private fb: TypedFormBuilder,
    private reportApi: ReportApi,
    private calendarService: CalendarService,
  ) {}

  ngOnDestroy() {
    this._subscriptions.forEach(s => s.unsubscribe());
  }

  ngOnInit() {
    // ensure that we only get the regimes from the selected registers
    // otherwise this will default to the 1st register which will almost certainy be the organisation countru
    // PLEASE NOTE - this relies on the restriction that only multiple registers in the same country can be ticked.
    const selectedRegisters = this.registers.filter(r => r._selected);
    this.firstSelectedRegister = selectedRegisters[0];
    this.regimes = this.firstSelectedRegister.regimes;

    this.financialYearStart = {
      day: this.firstSelectedRegister.reportingYearStartDateDay,
      month: this.firstSelectedRegister.reportingYearStartDateMonth,
    };

    const startM = moment
      .utc()
      .month(this.firstSelectedRegister.reportingYearStartDateMonth - 1)
      .date(this.firstSelectedRegister.reportingYearStartDateDay);

    this.firstYearStartDate = toUniversalDateString(startM, { throwOnFailure: true });

    const startDate = toUniversalDateString(startM, {
      throwOnFailure: true,
    });

    const endM = startM.add(1, 'years').add(-1, 'days');
    let endDate = toUniversalDateString(endM, {
      throwOnFailure: true,
    });

    if (endDate < startDate) {
      endDate = toUniversalDateString(moment.utc(endDate).add(1, 'year'), { throwOnFailure: true });
    }

    this.form.controls.regimeId.setValue(this.firstSelectedRegister.regimes[0].id);
    this.form.controls.periodStartDate.setValue(startDate);
    this.form.controls.periodEndDate.setValue(endDate);

    this._subscriptions.push(
      this.calendarService
        .getCalendarPeriods({
          registerId: this.firstSelectedRegister.id,
          yearsBefore: 20,
          yearsAfter: 20,
        })
        .subscribe(calendarPeriods => {
          this.endDates = calendarPeriods.map(cp =>
            toUniversalDateString(cp.endDate, { throwOnFailure: true }),
          );
          this.startDates = calendarPeriods.map(cp =>
            toUniversalDateString(cp.startDate, { throwOnFailure: true }),
          );
        }),
    );
  }

  onClose() {
    this.closePanel.emit();
  }

  private handleReportRedirect({ requestId }: QueueReportResponse) {
    // close the panel and pass the requestId
    this.closePanel.emit(requestId);
  }

  generateReport({ handle }: ValidSubmitEvent) {
    const formValue = this.form.value as ReportFormValue;
    // the format will not automatically be extracted from form.value if it is disabled
    const format = this.form?.controls?.format?.value ?? (ReportFormat.Pdf as ReportFormat); // default to pdf if undefined
    const consolidationAction =
      this.form?.controls?.consolidationAction?.value ??
      (ReportConsolidationAction.Merge as ReportConsolidationAction); // default to merge if undefined
    const registerIds = this.registers.filter(r => r._selected).map(r => r.id);

    handle(this.getReportResponse(formValue, registerIds, format, consolidationAction)).subscribe(
      consolidatedReportResponse => {
        this.handleReportRedirect(consolidatedReportResponse);
      },
    );
  }

  getReportResponse(
    formValue: ReportFormValue,
    registers: number[],
    format: ReportFormat,
    consolidationAction: ReportConsolidationAction,
  ): Observable<QueueReportResponse> {
    // splice in the format in case it is disabled on form
    const { reportType, ...command } = { ...formValue, registers, format, consolidationAction };

    switch (reportType) {
      case ReportType.AssetExport:
        return this.reportApi.generateConsolidatedAssetExportReport(command);

      case ReportType.AssetGroupSummary:
        return this.reportApi.generateConsolidatedAssetGroupSummaryReport(command);

      case ReportType.DepreciationForecast:
        return this.reportApi.generateConsolidatedDepreciationForecastReport(command);

      default:
        assertNever(reportType, 'Unrecognised consolidated report type: ' + reportType);
    }
  }
}
